import { useContext, useState } from "react";
import { SessionContext } from "../SessionContext";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Section from "./Section";
import { User } from "../types";

const Layout = ({ version }: { version: string }) => {
  document.title = "ASM";

  const [showMenu, setShowMenu] = useState(false);
  const sessionContext = useContext(SessionContext);
  const navigate = useNavigate();
  const location = useLocation();

  const logout = () => {
    sessionContext.logout();
    navigate("/");
  };

  const toggleMenu = () => setShowMenu((showMenu) => !showMenu);

  return (
    <div className="text-gray-900 flex flex-col">
      <div className="absolute bottom-0 left-0 text-xs text-white px-1">
        {version}
      </div>

      <div className="border-b border-slate-500 sticky top-0 bg-white z-30">
        <Section>
          <div className="flex">
            <Link
              className="p-4 hover:text-sky-600 font-bold"
              to="/"
              onClick={() => setShowMenu(false)}
            >
              {process.env.REACT_APP_NAME}
            </Link>

            <div className="flex items-center text-sm text-slate-400 italic">
              <PathName
                path={location.pathname}
                onClick={() => setShowMenu(false)}
              />
            </div>

            <div className="flex-1"></div>

            {sessionContext.user && (
              <div className="flex items-center cursor-default">
                <div className="py-1 px-3 bg-slate-200 text-xs text-gray-600 rounded-lg">
                  <FontAwesomeIcon className="mr-2" icon="user" />
                  <span className="uppercase font-mono">
                    {sessionContext.user.username}
                  </span>
                </div>
              </div>
            )}

            {sessionContext.user && (
              <div
                className="p-4 cursor-pointer hover:text-sky-600"
                onClick={toggleMenu}
              >
                <FontAwesomeIcon icon="bars" />
              </div>
            )}
          </div>
        </Section>
      </div>

      {showMenu && (
        <Menu
          user={sessionContext.user!}
          setShowMenu={setShowMenu}
          logout={logout}
        />
      )}

      {showMenu && (
        <div
          className="bg-black opacity-40 absolute top-0 left-0 w-full min-h-[100vh] z-10"
          onClick={() => setShowMenu(false)}
        />
      )}

      <div
        className="flex-1 absolute pt-[57px] overflow-scroll no-scrollbar left-0 w-full h-screen"
        onClick={() => setShowMenu(false)}
      >
        <Outlet />
      </div>
    </div>
  );
};

export default Layout;

const Menu = ({
  user,
  setShowMenu,
  logout,
}: {
  user: User;
  setShowMenu: (showMenu: boolean) => void;
  logout: () => void;
}) => {
  return (
    <div className="sticky top-[57px] left-0 w-full bg-white border-b z-20">
      <Section className="flex flex-col items-end font-semibold text-sm">
        {user.permissions.registrations && (
          <Link
            className="py-3 px-4 hover:text-sky-600"
            to="/clientes"
            onClick={() => setShowMenu(false)}
          >
            Clientes
          </Link>
        )}

        {user.permissions.receipts && (
          <Link
            className="py-3 px-4 hover:text-sky-600"
            to="/financeiro"
            onClick={() => setShowMenu(false)}
          >
            Financeiro
          </Link>
        )}

        {(user.permissions.myQuotes || user.permissions.quotesAdmin) && (
          <Link
            className="py-3 px-4 hover:text-sky-600"
            to="/meus-orcamentos"
            onClick={() => setShowMenu(false)}
          >
            Meus orçamentos
          </Link>
        )}

        {user.permissions.quotesAdmin && (
          <Link
            className="py-3 px-4 hover:text-sky-600"
            to="/aprovar-orcamentos"
            onClick={() => setShowMenu(false)}
          >
            Aprovar orçamentos
          </Link>
        )}

        {user.permissions.authorizations && (
          <Link
            className="py-3 px-4 hover:text-sky-600"
            to="/autorizacoes"
            onClick={() => setShowMenu(false)}
          >
            Autorizações
          </Link>
        )}

        {user.permissions.contracts && (
          <Link
            className="py-3 px-4 hover:text-sky-600"
            to="/contratos"
            onClick={() => setShowMenu(false)}
          >
            Contratos
          </Link>
        )}

        {user.permissions.installations && (
          <Link
            className="py-3 px-4 hover:text-sky-600"
            to="/instalacoes"
            onClick={() => setShowMenu(false)}
          >
            Instalações
          </Link>
        )}

        {user.permissions.orders && (
          <Link
            className="py-3 px-4 hover:text-sky-600"
            to="/os"
            onClick={() => setShowMenu(false)}
          >
            Ordens de serviço
          </Link>
        )}

        {user.permissions.stats && (
          <Link
            className="py-3 px-4 hover:text-sky-600"
            to="/estatisticas"
            onClick={() => setShowMenu(false)}
          >
            Estatísticas
          </Link>
        )}

        {user.permissions.equipments && (
          <Link
            className="py-3 px-4 hover:text-sky-600"
            to="/equipamentos"
            onClick={() => setShowMenu(false)}
          >
            Equipamentos
          </Link>
        )}

        {user.permissions.users && (
          <Link
            className="py-3 px-4 hover:text-sky-600"
            to="/usuarios"
            onClick={() => setShowMenu(false)}
          >
            Usuários
          </Link>
        )}

        <div
          className="py-3 px-4 border-t hover:text-sky-600 cursor-pointer w-full text-right"
          onClick={() => {
            setShowMenu(false);
            logout();
          }}
        >
          Sair
        </div>
      </Section>
    </div>
  );
};

const PathName = ({ path, onClick }: { path: string; onClick: () => void }) => (
  <div>
    {path.startsWith("/preferencias") && (
      <Link className="hover:text-sky-500" to="/preferencias" onClick={onClick}>
        / preferências
      </Link>
    )}

    {path.startsWith("/clientes") && (
      <Link className="hover:text-sky-500" to="/clientes" onClick={onClick}>
        / clientes
      </Link>
    )}
    {path.startsWith("/financeiro") && (
      <Link className="hover:text-sky-500" to="/financeiro" onClick={onClick}>
        / financeiro
      </Link>
    )}
    {path.startsWith("/meus-orcamentos") && (
      <Link
        className="hover:text-sky-500"
        to="/meus-orcamentos"
        onClick={onClick}
      >
        / meus orçamentos
      </Link>
    )}
    {path.startsWith("/aprovar-orcamentos") && (
      <Link
        className="hover:text-sky-500"
        to="/aprovar-orcamentos"
        onClick={onClick}
      >
        / aprovar orçamentos
      </Link>
    )}
    {path.startsWith("/autorizacoes") && (
      <Link className="hover:text-sky-500" to="/autorizacoes" onClick={onClick}>
        / autorizações
      </Link>
    )}
    {path.startsWith("/contratos") && (
      <Link className="hover:text-sky-500" to="/contratos" onClick={onClick}>
        / contratos
      </Link>
    )}
    {path.startsWith("/instalacoes") && (
      <Link className="hover:text-sky-500" to="/instalacoes" onClick={onClick}>
        / instalações
      </Link>
    )}
    {path.startsWith("/os") && (
      <Link className="hover:text-sky-500" to="/os" onClick={onClick}>
        / ordens de serviço
      </Link>
    )}
    {path.startsWith("/estatisticas") && (
      <Link className="hover:text-sky-500" to="/estatisticas" onClick={onClick}>
        / estatísticas
      </Link>
    )}
    {path.startsWith("/usuarios") && (
      <Link className="hover:text-sky-500" to="/usuarios" onClick={onClick}>
        / usuários
      </Link>
    )}
    {path.startsWith("/equipamentos") && (
      <Link className="hover:text-sky-500" to="/equipamentos" onClick={onClick}>
        / equipamentos
      </Link>
    )}
  </div>
);
